import type { ReactNode } from "react";
import { ChevronDown, ChevronRight } from "react-feather";
import { Button, Tooltip } from "@bfc-software/pallet";
import type { TFunction } from "i18next";
import * as dateFns from "date-fns";

import type { PlanRow } from "../-data/plans-model";

import { PlanStatusTag, Progress, AutoTags } from "./task-bits";
import styles from "./plan-card.module.css";

/**
 * One task plan.
 *
 * The card is where activation lives, because activation is a decision about a
 * whole wave - releasing tasks one at a time is not something the floor does, and
 * a per-row control would invite it. Everything else on the card exists to make
 * that decision: how much work it releases, which routes it serves, and how far
 * the plan already got.
 *
 * All of that comes from the plan listing alone. `TaskPlanActivationState` rolls
 * up the counts, so a collapsed card costs nothing beyond the row it was drawn
 * from - which is what lets the tasks be fetched only on expansion.
 *
 * The card does not fetch them. `tasks` is handed in already-rendered and only
 * mounted while expanded, so the read lives with the route that owns the query
 * and this file stays a drawing of a `PlanRow`.
 */
export function PlanCard({
  row,
  isExpanded,
  onToggle,
  onActivate,
  onDeactivate,
  isPending,
  tasks,
  t,
}: {
  row: PlanRow;
  isExpanded: boolean;
  onToggle: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  /** An activation is in flight for this plan; only its buttons disable. */
  isPending: boolean;
  /** The expanded table. Rendered only while open, so its query is too. */
  tasks: ReactNode;
  t: TFunction;
}) {
  const { plan } = row;

  /**
   * What the action column says under the button, or in place of it.
   *
   * The note is the part that carries the reason: a plan with no button and no
   * note is indistinguishable from a plan whose button failed to render.
   */
  const note = row.canActivate
    ? t("tasks.plans.note.releases", { count: row.taskCount })
    : row.statusKey === "completed"
      ? t("tasks.plans.note.complete")
      : row.statusKey === "activating"
        ? t("tasks.plans.note.activating")
        : row.isStarted
          ? t("tasks.plans.note.locked")
          : "";

  return (
    <div className={styles.card} data-test-role="plan-card" data-plan={plan.id}>
      <div className={styles.summary}>
        {/*
          Every card gets a chevron, whatever its activation state (§0 of the
          implementation plan). The planning worker writes a plan's task rows
          once, at plan time, and activation is a later, separate fact about
          them - so a plan nobody has released still has tasks behind it, just
          read from a different endpoint (`GET /{orgId}/tasks` rather than the
          activation listing; see `usePlanTasks` in `use-plans.ts`). The only
          card with nothing to expand onto is one whose own *planning* produced
          no rows, and that already draws as the ordinary empty state inside an
          expanded card rather than as a missing chevron - auditing why
          planning failed is explicitly out of scope here.
        */}
        <button
          type="button"
          className={styles.chevron}
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-label={t(
            isExpanded ? "tasks.plans.collapse" : "tasks.plans.expand",
          )}
          data-test-role="plan-toggle"
        >
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        <div className={styles.identity}>
          <div className={styles.identityTop}>
            <span className={styles.planId}>{plan.name}</span>
            <PlanStatusTag statusKey={row.statusKey} t={t} />
            {/*
              Formatted here rather than through the shared `DateValue`, because
              the date is inside a sentence - "Created 14 Jul 2026" is one string
              a translator has to be able to reorder, and DateValue renders a
              block of its own that cannot sit inside one.
            */}
            <span className={styles.created}>
              {t("tasks.plans.created", {
                date: dateFns.format(dateFns.parseISO(plan.createdAt), "PP"),
              })}
            </span>
          </div>
          <div className={styles.identityBottom}>
            <span className={styles.taskCount}>
              {t("tasks.plans.taskCount", { count: row.taskCount })}
            </span>
            <span className={styles.divider} aria-hidden="true" />
            {/*
              The routes the wave serves, which is the one thing a collapsed card
              can say about where the work is: it comes off the plan itself, so it
              needs none of the task reads an expanded card makes.
            */}
            <AutoTags labels={plan.routeCodes} />
          </div>
        </div>

        <div className={styles.meter}>
          {/*
            Tasks done out of tasks released, not picks: the plan listing rolls
            up task states and nothing else, and a pick total on a collapsed card
            would have to come from reading every task in the plan.
          */}
          <Progress
            done={row.tasksDone}
            total={row.tasksTotal}
            percent={row.percent}
            wide={true}
            label={t("tasks.plans.tasksProgress", {
              done: row.tasksDone,
              total: row.tasksTotal,
            })}
          />
        </div>

        <div className={styles.action}>
          {row.canActivate ? (
            // Inline scale, matching the deactivate button in the strip below.
            // A standard-scale button is as tall as an input, which on a summary
            // row of 13px text reads as a form control that wandered in.
            <Button
              variant="primary"
              text={t("tasks.plans.activate")}
              isDisabled={isPending}
              onPress={onActivate}
              display={{ scale: "inline" }}
            />
          ) : (
            <></>
          )}
          {note ? <span className={styles.actionNote}>{note}</span> : <></>}
        </div>
      </div>

      {isExpanded ? (
        <>
          {/*
            Deactivation is behind the expansion on purpose. Pulling a wave back
            is rare and disruptive, so it sits where you have already looked at
            what is in the plan - not next to the button you press all day.
          */}
          {plan.activation.kind === "released" &&
          row.statusKey !== "completed" ? (
            <div className={styles.deactivateStrip}>
              {row.canDeactivate ? (
                <Button
                  variant="danger"
                  text={t("tasks.plans.deactivate")}
                  isDisabled={isPending}
                  onPress={onDeactivate}
                  display={{ scale: "inline" }}
                />
              ) : (
                // Disabled with the reason attached, rather than absent: a
                // manager looking for the button needs to be told why picking
                // has taken it away, not left wondering where it went.
                <Tooltip
                  content={t("tasks.plans.deactivateTooltip.locked")}
                  trigger="span"
                >
                  <span data-test-role="plan-deactivate-locked">
                    <Button
                      variant="danger"
                      text={t("tasks.plans.deactivate")}
                      isDisabled={true}
                      display={{ scale: "inline" }}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          ) : (
            <></>
          )}

          <div className={styles.tasks}>{tasks}</div>
        </>
      ) : (
        <></>
      )}
    </div>
  );
}
